import React from "react";
import useMediaQuery from "@mui/system/useMediaQuery";
import { Typography, useTheme, Grid } from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import { Button, CircularProgress } from "@mui/material";
import { DeleteRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";

interface Props {
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmLabel?: string;
    loading?: boolean;
    onConfirm: () => void;
    onClose: () => void;
}

/**
 * Generic delete confirmation dialog. The caller owns the mutation (wires it to
 * `onConfirm` and passes `loading`); this component only renders the confirmation UI.
 */
const ConfirmDeleteModal = ({ open, title, message, confirmLabel, loading, onConfirm, onClose }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            fullScreen={isFullScreen}
            slotProps={{
                paper: { sx: { minWidth: isFullScreen ? undefined : "460px" } },
            }}
        >
            <BasicModalContent
                startIcon={<DeleteRounded />}
                onClose={loading ? undefined : onClose}
                title={<Typography level="titleSmallLight">{title}</Typography>}
            >
                <Box borderRadius="4px" border="1px solid #ccc" padding="12px 16px" marginTop="8px">
                    <Typography level="body-md">{message}</Typography>
                </Box>

                <Grid container spacing={1} sx={{ pt: "16px" }}>
                    <Grid xs={6}>
                        <Button fullWidth variant="outlined" onClick={onClose} disabled={loading}>
                            {t("Cancel")}
                        </Button>
                    </Grid>
                    <Grid xs={6}>
                        <Button
                            variant="contained"
                            color="error"
                            fullWidth
                            onClick={onConfirm}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : <DeleteRounded />}
                        >
                            {confirmLabel ?? t("Delete")}
                        </Button>
                    </Grid>
                </Grid>
            </BasicModalContent>
        </Dialog>
    );
};

export default ConfirmDeleteModal;
